const esrever = require('esrever');

/**
 * Extracts a grapheme cluster from the given text at the specified index.
 * @param {string} text - Input text.
 * @param {number} index - Index of the grapheme to extract.
 * @returns string
 */
function readEmojiClusterFromPaths(text, index) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    const graphemeClusters = Array.from(segmenter.segment(text)).map((s) => s.segment);
    const cluster = graphemeClusters[index] || '';

    if (!cluster) {
        return '';
    }
    return cluster;
}

/**
 * Calculates the bounding box from a set of path commands.
 * @param {Array} cmds - Array of path commands.
 * @returns {object|null} Bounding box with minX, minY, maxX, maxY, w, h or null if no valid coordinates.
 */
function boxFromCmds(cmds) {
    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
    for (const c of cmds || []) {
        // Check all possible coordinate pairs in the command
        [
            ['x', 'y'],
            ['x1', 'y1'],
            ['x2', 'y2']
        ].forEach(([xKey, yKey]) => {
            if (xKey in c && yKey in c) {
                const x = c[xKey],
                    y = c[yKey];
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
            }
        });
    }
    if (!Number.isFinite(minX)) return null;
    const calculatedW = Math.max(0, maxX - minX);
    const calculatedH = Math.max(0, maxY - minY);
    return {
        minX,
        minY,
        maxX,
        maxY,
        w: calculatedW,
        h: calculatedH
    };
}

/**
 * Draws an emoji cluster on the canvas context, fitting it inside the given box.
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {string} cluster - The emoji cluster to draw
 * @param {number} x - X offset
 * @param {number} y - Y offset
 * @param {object} box - Bounding box with minX, minY, w, h
 * @param {number} fontSize - Base font size
 */
function drawEmoji(options) {
    const { ctx, cluster, x, y, box, fontSize } = options;

    // Emoji font family for consistent rendering across platforms
    const family = '"Noto Color Emoji","Apple Color Emoji","Segoe UI Emoji",sans-serif';

    // If bounding box or font size is invalid, do nothing
    if (!box?.w || !box?.h || !fontSize) {
        return;
    }

    ctx.save();
    const originalFont = ctx.font;

    // Set font to initial fontSize for measurement
    ctx.font = `${fontSize}px ${family}`;
    // Measure emoji metrics at initial size
    const m0 = ctx.measureText(cluster);

    // Calculate the actual visual width using actualBoundingBoxLeft and actualBoundingBoxRight
    const advW0 = Math.max(1, m0.width);
    const left = m0.actualBoundingBoxLeft ?? 0;
    const right = m0.actualBoundingBoxRight ?? advW0;

    // Calculate the glyph width
    const glyphW0 = Math.max(left + right, advW0);

    const ascent0 = m0.actualBoundingBoxAscent ?? fontSize * 0.8;
    const descent0 = m0.actualBoundingBoxDescent ?? fontSize * 0.2;
    const inkH0 = Math.max(1, ascent0 + descent0);

    // Calculate scale using the actual visual width
    const scale = Math.min(box.w / glyphW0, box.h / inkH0);
    const emojiPx = fontSize * scale;

    const glyphW1 = glyphW0 * scale;
    const left1 = left * scale;

    // Center the emoji horizontally in the box
    const drawX = x + box.minX + (box.w - glyphW1) / 2 + left1;
    // Align the emoji to the bottom of the box
    const descent1 = descent0 * scale;
    const drawY = y + box.minY + box.h - descent1;

    // Set font to scaled size and draw the emoji
    ctx.font = `${emojiPx}px ${family}`;
    (ctx._fillText ?? ctx.fillText).call(ctx, cluster, drawX, drawY);

    ctx.restore();
    ctx.font = originalFont;
}

export const isSpaceChar = function (char) {
    return ['\r', '\n', '\t', '\f', '\v', ' '].indexOf(char) > -1;
};

export const splitWords = function (text) {
    const result = [];
    let currentWord = '';

    if (!text) {
        return [''];
    }

    for (const letter of text) {
        if (isSpaceChar(letter)) {
            if (currentWord) {
                result.push(currentWord);
            }

            result.push(letter);
            currentWord = '';
        } else {
            currentWord += letter;
        }
    }

    if (currentWord) {
        result.push(currentWord);
    }

    return result;
};

/**
 * Returns the texture pre-composited over the current solid fill color, so textured text is filled once.
 * Filling the color and then the texture on the same path leaves a dark antialiasing seam along the glyph edges.
 * @param {CanvasRenderingContext2D} ctx
 * @returns {CanvasPattern|null} null when there is no texture or the fill is not a plain color
 */
const getTextureOverColorPattern = (() => {
    // Only the last color is kept per texture so memory doesn't grow while a customer tries colors
    const cache = new WeakMap(); // texture image -> { color, canvas }

    return function (ctx) {
        const texture = ctx._fillPatternImage && ctx._fillPatternImage.source;
        const color = ctx.fillStyle;
        if (
            !ctx._fillPattern ||
            !texture ||
            !texture.width ||
            !texture.height ||
            ctx._fillGradient ||
            typeof color !== 'string'
        ) {
            return null;
        }

        let composited = cache.get(texture);
        if (!composited) {
            composited = { color: null, canvas: fabric.util.createCanvasElement() };
            cache.set(texture, composited);
        }
        if (composited.color !== color) {
            const { canvas } = composited;
            canvas.width = texture.width;
            canvas.height = texture.height;
            const compositedCtx = canvas.getContext('2d');
            compositedCtx.fillStyle = color;
            compositedCtx.fillRect(0, 0, canvas.width, canvas.height);
            compositedCtx.drawImage(texture, 0, 0);
            composited.color = color;
        }
        return ctx.createPattern(composited.canvas, ctx._fillPatternImage.repeat || 'repeat');
    };
})();

export const drawPaths = function (options) {
    const {
        paths,
        ctx,
        drawStroke,
        drawFill,
        drawShadow,
        drawShadowWithFill,
        text = undefined,
        paletteStart = 0
    } = options;

    if (!drawFill && !drawStroke && !drawShadow) {
        return paletteStart;
    }

    // Combine all glyphs into a single path so when calling fill() or stroke() the entire
    //  text is rendered
    const singlePath = !(
        ctx._fillPaletteColor &&
        ctx._fillPaletteColor.colors &&
        ctx._fillPaletteColor.colors.length > 1
    );
    if (singlePath) ctx.beginPath();
    let index = paletteStart;
    for (let n = 0; n < paths.length; ++n) {
        if (!singlePath) ctx.beginPath();
        const path = paths[n];
        var x = path.offsetX;
        var y = path.offsetY;

        if (path.character && text !== undefined) {
            const isEmoji = path.dataGlyphType === 'emoji-tofu';
            if (isEmoji) {
                const cluster = readEmojiClusterFromPaths(text, n);
                const box = boxFromCmds(path.commands);
                if (!box) {
                    // No valid bounding box, skip drawing this emoji
                    continue;
                }
                const basePx = parseFloat((/(\d+(?:\.\d+)?)px/.exec(ctx.font) || ['', '16'])[1]);
                const fontSize = basePx;
                drawEmoji({
                    ctx,
                    cluster,
                    x,
                    y,
                    box,
                    fontSize
                });
                continue;
            }
        }

        const glyphColor =
            !singlePath && path?.commands?.length > 0 ? ctx._fillPaletteColor.getColor(index++) : null;
        if (glyphColor && drawFill) {
            ctx.fillStyle = glyphColor;
        }
        for (var i = 0; i < path.commands.length; i += 1) {
            var cmd = path.commands[i];
            if (cmd.type === 'M') {
                ctx.moveTo(cmd.x + x, cmd.y + y);
            } else if (cmd.type === 'L') {
                ctx.lineTo(cmd.x + x, cmd.y + y);
            } else if (cmd.type === 'C') {
                ctx.bezierCurveTo(cmd.x1 + x, cmd.y1 + y, cmd.x2 + x, cmd.y2 + y, cmd.x + x, cmd.y + y);
            } else if (cmd.type === 'Q') {
                ctx.quadraticCurveTo(cmd.x1 + x, cmd.y1 + y, cmd.x + x, cmd.y + y);
            } else if (cmd.type === 'Z') {
                ctx.closePath();
            }
        }
        if (!singlePath && drawShadow && ctx._fillShadow != null) {
            ctx.save();
            // fillStyle is transparent, so pallete color is assigned to draw the shadow
            if (glyphColor) {
                ctx.fillStyle = glyphColor;
            }
            applyShadowFromContext(ctx);

            if (drawStroke && ctx.lineWidth) {
                ctx.stroke();
            }

            ctx.fill();
            ctx.restore();
        }
        if (!singlePath && drawFill) {
            ctx.fill();
        }
        if (!singlePath && ctx.lineWidth && drawStroke) {
            ctx.stroke();
        }
    }
    if (singlePath) {
        ctx.closePath();
    }

    if (singlePath && drawShadow && ctx._fillShadow != null) {
        ctx.save();
        if (ctx._fillGradient) {
            // fillStyle is transparent, so the gradient is assigned to draw the shadow
            ctx.fillStyle = ctx._fillGradient.toLive(ctx);
        }
        applyShadowFromContext(ctx);

        if (drawStroke && ctx.lineWidth) {
            ctx.stroke();
        }

        ctx.fill();
        ctx.restore();
    }

    if (singlePath && ctx.lineWidth && drawStroke) {
        ctx.stroke();
    }

    if (singlePath && ctx.fillStyle && drawFill) {
        const textureOverColor = getTextureOverColorPattern(ctx);
        if (textureOverColor) {
            ctx.save();
            ctx.fillStyle = textureOverColor;
            const m = ctx._fillPatternImage.patternTransform;
            m && ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
        }

        if (drawShadowWithFill && ctx._fillShadow != null) {
            ctx.save();
            if (ctx._fillGradient) {
                // fillStyle is transparent, so the gradient is assigned to draw the shadow
                ctx.fillStyle = ctx._fillGradient.toLive(ctx);
            }
            applyShadowFromContext(ctx);
            ctx.fill();
            ctx.restore();
        } else {
            ctx.fill();
        }

        if (textureOverColor) {
            ctx.restore();
        } else if (ctx._fillPattern) {
            ctx.save();
            ctx.fillStyle = ctx._fillPattern;
            var m = ctx._fillPatternImage.patternTransform;
            m && ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
            ctx.fill();
            ctx.restore();
        }

        if (ctx._fillGradient) {
            ctx.save();
            ctx.fillStyle = ctx._fillGradient.toLive(ctx);
            if (ctx._fillGradientTransform) {
                const m = ctx._fillGradientTransform;
                m && ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
            }
            const m1 = ctx._fillGradient.gradientTransform;
            m1 && ctx.transform(m1[0], m1[1], m1[2], m1[3], m1[4], m1[5]);
            ctx.fill();
            ctx.restore();
        }
    }

    return index;
};

/**
 * Applies shadow settings from the drawing context's internal `_fillShadow` property
 * to the standard canvas shadow properties.
 *
 * This function expects the context to have a `_fillShadow` object with the
 * properties `color`, `blur`, `offsetX`, and `offsetY`. Missing values are
 * defaulted to an empty string for color and `0` for numeric properties.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D rendering context containing a `_fillShadow` configuration.
 * @returns {void}
 */
export const applyShadowFromContext = function (ctx) {
    ctx.shadowColor = ctx._fillShadow.color || '';
    ctx.shadowBlur = ctx._fillShadow.blur || 0;
    ctx.shadowOffsetX = ctx._fillShadow.offsetX || 0;
    ctx.shadowOffsetY = ctx._fillShadow.offsetY || 0;
};

/**
 * Calculates the stroke width of both the left and right text (letters).
 * It scans the pixels of both letters to compute the stroke widths for each side.
 * For this to work the text and stroke colors MUST be different.
 * @param {string} letterLeft text for the left side (usually a single letter)
 * @param {string} letterRight text for the right side (usually a single letter)
 * @param {Object} font opentype Font object
 * @param {number} fontSize font size from the given canvas context
 * @param {HTMLCanvasElement} tempCanvas canvas to use to draw the text and compute the stroke width
 * @param {number} strokeWidth stroke width
 * @param {boolean} kerned true if text rendering should use font kerning
 * @param {number} tracking amount of tracking
 * @param {boolean} ligatures if ligatures should be used
 * @returns {Object} An object containing both left and right stroke widths
 */
export const computeStroke = (() => {
    const cache = new Map();
    const cleanupTimeouts = new Map();

    const scheduleCleanupForKey = (key) => {
        // Cancel any existing timeout for this key
        if (cleanupTimeouts.has(key)) {
            clearTimeout(cleanupTimeouts.get(key));
        }

        // Schedule a new timeout to clean up this cache entry after 50ms
        const timeout = setTimeout(() => {
            cache.delete(key);
            cleanupTimeouts.delete(key);
        }, 50);

        cleanupTimeouts.set(key, timeout);
    };

    return function (letterLeft, letterRight, font, fontSize, tempCanvas, strokeWidth, kerned, tracking, ligatures) {
        // Create a unique key for deduplication based on all parameters that affect the result
        const key = `computeStroke_${letterLeft}_${letterRight}_${
            font.familyName || 'unknown'
        }_${fontSize}_${strokeWidth}_${kerned}_${tracking}_${ligatures}`;

        // Check if we already have this result cached
        if (cache.has(key)) {
            scheduleCleanupForKey(key);
            return cache.get(key);
        }

        const result = computeStrokeInternal(
            letterLeft,
            letterRight,
            font,
            fontSize,
            tempCanvas,
            strokeWidth,
            kerned,
            tracking,
            ligatures
        );

        // Cache the result
        cache.set(key, result);

        // Schedule cleanup for this key
        scheduleCleanupForKey(key);

        return result;
    };
})();

/**
 * Internal implementation of computeStroke (extracted to support deduplication)
 * @private
 */
const computeStrokeInternal = function (
    letterLeft,
    letterRight,
    font,
    fontSize,
    tempCanvas,
    strokeWidth,
    kerned,
    tracking,
    ligatures
) {
    // Define the colors to use to render the text and the stroke
    const fill = '#000000';
    const stroke = '#ff0000';
    const fontColor = fabric.Color.sourceFromHex(fill);

    // Helper function to calculate the stroke width for a given path
    const calculateStrokeWidth = (path, direction = 'left') => {
        // Get the bounding box and set up canvas size
        const bb = path.getBoundingBox();
        tempCanvas.width = bb.x2 - bb.x1 + 3 * strokeWidth + 2;
        tempCanvas.height = bb.y2 - bb.y1 + 3 * strokeWidth + 2;

        const canvasCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
        canvasCtx.lineJoin = 'round';
        canvasCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
        canvasCtx.fillStyle = fill;
        canvasCtx.strokeStyle = strokeWidth > 0 ? stroke : null;
        canvasCtx.lineWidth = strokeWidth;

        // Draw the glyph
        path.offsetX = strokeWidth * 2;
        path.offsetY = tempCanvas.height - strokeWidth;
        drawPaths({
            paths: [path],
            ctx: canvasCtx,
            drawStroke: true,
            drawFill: true,
            text: undefined
        });

        // Get image data
        const imgData = canvasCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const data = imgData.data;

        // Variables to store stroke width
        let x1 = -1,
            x2 = -1;
        let computedStrokeWidth = 0;

        // Scan pixels
        const start = direction === 'left' ? 0 : tempCanvas.width - 1;
        const end = direction === 'left' ? tempCanvas.width : -1;
        const step = direction === 'left' ? 1 : -1;

        for (let col = start; col !== end; col += step) {
            for (let row = 0; row < tempCanvas.height; ++row) {
                const index = row * (tempCanvas.width * 4) + col * 4;
                const red = data[index];
                const green = data[index + 1];
                const blue = data[index + 2];
                const alpha = data[index + 3];

                if (alpha !== 0x00 && red === fontColor[0] && green === fontColor[1] && blue === fontColor[2]) {
                    x2 = col;
                    if (x1 !== -1) {
                        computedStrokeWidth = Math.abs(x2 - x1) + 1;
                    }
                    break;
                } else if (x1 === -1 && alpha !== 0x00) {
                    x1 = col;
                }
            }
            if (computedStrokeWidth > 0) break;
        }

        return computedStrokeWidth;
    };

    // Get paths for both letters
    const pathLeft = font.getPath(letterLeft, 0, 0, fontSize, {
        drawSVG: false,
        drawLayers: false,
        kerning: kerned,
        tracking: tracking,
        features: {
            liga: ligatures,
            rlig: ligatures
        }
    });

    const pathRight = font.getPath(letterRight, 0, 0, fontSize, {
        drawSVG: false,
        drawLayers: false,
        kerning: kerned,
        tracking: tracking,
        features: {
            liga: ligatures,
            rlig: ligatures
        }
    });

    // Calculate stroke widths for both sides
    const leftStrokeWidth = calculateStrokeWidth(pathLeft, 'left');
    const rightStrokeWidth = calculateStrokeWidth(pathRight, 'right');

    return {
        leftStrokeWidth: leftStrokeWidth,
        rightStrokeWidth: rightStrokeWidth
    };
};

/**
 * Reverse text
 * @param text
 * @returns {string}
 */
export const reverseText = function (text) {
    if (text.indexOf('\n') > -1) {
        const stringArray = text.split('\n');
        let result = '';
        stringArray.forEach(function (s) {
            result += esrever.reverse(s) + '\n';
        });
        return result.slice(0, -1);
    }
    return esrever.reverse(text);
};

export const getVerticalText = function (text) {
    if (!text) {
        return text;
    }

    // Use Intl.Segmenter for proper grapheme segmentation (respects order)
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    const segments = Array.from(segmenter.segment(text)).map((s) => s.segment);

    return segments.join('\n');
};
